import { useEffect } from 'react';
import { getAgreementType, type TradeType } from '@/types';
import type { Counterparty } from '@/types/counterparty.types';

/**
 * The booking's own refusal (`counterparty_name`) when the counterparty holds no agreement of the
 * trade's product, or null. A counterparty the registry does not list is left to the booking.
 */
export function missingAgreementMessage(
  counterparties: Counterparty[],
  counterpartyName: string | undefined,
  tradeType: string | undefined,
): string | null {
  const counterparty = counterparties.find((cp) => cp.name === counterpartyName);
  if (!counterparty || !tradeType) return null;
  const product = getAgreementType(tradeType as TradeType);
  if (counterparty.agreements.some((agreement) => agreement.type === product)) return null;
  return (
    `${counterparty.name} has no ${product.toUpperCase()} agreement, so a ${tradeType} trade ` +
    'cannot be booked with it. Add the agreement in the counterparty registry.'
  );
}

/** The currency a repo with this counterparty is margin-called in, or undefined for any other trade. */
export function repoCallCurrency(
  counterparties: Counterparty[],
  counterpartyName: string | undefined,
  tradeType: string | undefined,
): string | undefined {
  if (tradeType !== 'repo' && tradeType !== 'reverse_repo') return undefined;
  return counterparties
    .find((cp) => cp.name === counterpartyName)
    ?.agreements.find((agreement) => agreement.type === 'gmra')?.currency;
}

/** A warning, not a refusal: the rate may only be missing today. */
export function noRepoCallRateMessage(fromCurrency: string, toCurrency: string): string {
  return (
    `No closing (COB T-1) FX rate for ${fromCurrency}→${toCurrency} today: the margin call, ` +
    `which is in ${toCurrency}, cannot value this trade until there is one.`
  );
}

/**
 * While the refusal is shown, re-read the counterparties when the trader comes back to the tab:
 * the message sends them to the registry in another tab, and the shared hook neither refetches on
 * focus (see `useCounterparties`) nor goes stale for five minutes.
 */
export function useRefetchCounterpartiesOnReturn(
  active: boolean,
  refetch: (options: { cancelRefetch: boolean }) => unknown,
) {
  useEffect(() => {
    if (!active) return;
    const onReturn = () => {
      // A tab switch fires both events. Joining the fetch in flight rather than restarting it: the
      // registry pages without a signal, so a cancelled fetch still downloads every page.
      if (document.visibilityState === 'visible') void refetch({ cancelRefetch: false });
    };
    window.addEventListener('focus', onReturn);
    document.addEventListener('visibilitychange', onReturn);
    return () => {
      window.removeEventListener('focus', onReturn);
      document.removeEventListener('visibilitychange', onReturn);
    };
  }, [active, refetch]);
}
